// src/components/GlobalDesignBar.tsx
import React, { useState } from "react";
import { Box, Group, Tabs, TextInput, ActionIcon, Tooltip, Divider } from "@mantine/core";
import { FaSearch, FaSlidersH, FaRuler } from "react-icons/fa";
import { FiMaximize2, FiHelpCircle } from "react-icons/fi";
import { TbDragDrop } from "react-icons/tb";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setFreeTextSearchInRedux,
  setMeasurementToolActiveInRedux,
  setMouseHelpModalOpenInRedux,
  setRequestZoomExtentInRedux,
} from "../designSlice";
import { MULTIPLACER_MODULES } from "./designModules";
import { RotationControl } from "./RotationControl";
import { BuildingStoryMenu } from "./BuildingStoryMenu";
import { IfcClassesMenu } from "./IfcClassesMenu";
import { ColorFilterMenu } from "./ColorFilterMenu";
import { ModuleSelectionModal } from "./ModuleSelectionModal";
import { changeActiveModuleThunk } from "../designThunk";
import { DragSelectRoomsControl } from "./DragSelectRoomsControl";
import { MouseHelpModal } from "../../components/MouseHelpModal";

export const GlobalDesignBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const activeModule = useAppSelector((s) => s.design.activeModule);
  const freeTextSearch = useAppSelector((s) => s.design.freeTextSearch ?? "");
  const dragSelectSpaces = useAppSelector((s) => s.design.dragSelectSpaces);
  const measurementToolActive = useAppSelector((s) => s.design.measurementToolActive);
  const mouseHelpModalOpen = useAppSelector((s) => s.design.mouseHelpModalOpen);

  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Box
        bg="white"
        px="md"
        py={6}
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          zIndex: 10,
        }}
      >
        {mouseHelpModalOpen && (
          <MouseHelpModal
            opened={mouseHelpModalOpen}
            onClose={() => dispatch(setMouseHelpModalOpenInRedux(false))}
          />
        )}
        <Group justify="space-between" align="center" gap="md">
          {/* ===== VENSTRE: etasje + modul-kontekst ===== */}
          <Group gap="sm" align="center">
            <Tabs
              value={activeModule ?? null}
              onChange={(value) => dispatch(changeActiveModuleThunk(value as any))}
              variant="pills"
              color="blue"
              radius="xl"
              keepMounted={false}
            >
              <Tabs.List>
                {MULTIPLACER_MODULES.map((mod) => {
                  const Icon = mod.icon;
                  return (
                    <Tooltip
                      key={mod.key}
                      label={mod.description}
                      withArrow
                      position="bottom"
                      maw={320}
                      multiline
                    >
                      <Tabs.Tab
                        value={mod.key}
                        leftSection={<Icon size={18} />}
                        fw={600}
                        style={{
                          paddingTop: 10,
                          paddingBottom: 10,
                          paddingLeft: 18,
                          paddingRight: 18,
                          fontSize: 15,
                        }}
                      >
                        {mod.title}
                      </Tabs.Tab>
                    </Tooltip>
                  );
                })}
              </Tabs.List>
            </Tabs>

            <Tooltip label="Åpne detaljert modulvelger" withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                radius="xl"
                size={34}
                aria-label="Åpne modulvelger"
                onClick={() => setModalOpen(true)}
              >
                <FaSlidersH size={16} />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" h={24} />
            <BuildingStoryMenu />
            <Divider orientation="vertical" h={24} />
            <IfcClassesMenu />
            <Divider orientation="vertical" h={24} />
          </Group>

          {/* ===== HØYRE: søk + verktøy for visningen ===== */}
          <Group gap="sm" align="center">
            {activeModule !== "EXPLORE" && <ColorFilterMenu />}

            {/* Blokk 2: søk */}
            <TextInput
              style={{ width: 220 }}
              placeholder="Søk rom"
              value={freeTextSearch}
              onChange={(e) => dispatch(setFreeTextSearchInRedux(e.target.value))}
              size="sm"
              leftSection={<FaSearch size={12} />}
            />

            <Divider orientation="vertical" h={24} />

            {/* Blokk 3: rom-verkøy */}
            <DragSelectRoomsControl />

            <Tooltip label="Zoom extent" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                onClick={() => dispatch(setRequestZoomExtentInRedux())}
              >
                <FiMaximize2 size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Mål avstand" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                disabled={measurementToolActive || dragSelectSpaces}
                onClick={() => dispatch(setMeasurementToolActiveInRedux(true))}
              >
                <FaRuler size={16} />
              </ActionIcon>
            </Tooltip>

            <Divider orientation="vertical" h={24} />

            {/* Blokk 4: rotasjon */}
            <RotationControl />

            <Divider orientation="vertical" h={24} />

            {/* ✅ Hjelp (musekontroller) - lengst til høyre */}
            <Tooltip label="Musekontroller" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size={34}
                radius="xl"
                aria-label="Musekontroller"
                onClick={() => dispatch(setMouseHelpModalOpenInRedux(true))}
              >
                <FiHelpCircle size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {modalOpen && <ModuleSelectionModal onClose={() => setModalOpen(false)} />}
    </>
  );
};
